import {
  AgentRoleSchema,
  AgentSessionStatusSchema,
  ApplicationPhaseSchema,
  PaymentIntentStatusSchema,
  RecommendationSchema,
} from '@paguz/contracts';
import { z } from 'zod';

/** Filtros persistidos en la URL (§2). Valores inválidos se descartan sin romper la página. */
function lenient<T extends z.ZodRawShape>(shape: T) {
  type Out = z.infer<z.ZodObject<T>>;
  return (input: Record<string, unknown>): Out => {
    const out: Record<string, unknown> = {};
    for (const [key, field] of Object.entries(shape)) {
      const parsed = (field as z.ZodType).safeParse(input[key]);
      if (parsed.success && parsed.data !== undefined && parsed.data !== '') out[key] = parsed.data;
    }
    return out as Out;
  };
}

const optionalText = z.string().trim().max(200).optional();

export const validateTransactionsSearch = lenient({
  q: optionalText,
  status: PaymentIntentStatusSchema.optional(),
  reasonCode: optionalText,
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  minAmount: z.string().regex(/^\d+$/).optional(),
  maxAmount: z.string().regex(/^\d+$/).optional(),
  cursor: optionalText,
});
export type TransactionsSearch = ReturnType<typeof validateTransactionsSearch>;

export const validateCustomersSearch = lenient({ q: optionalText });

export const LINE_TABS = ['utilizaciones', 'cuotas', 'extractos', 'pagos', 'movimientos'] as const;
export type LineTab = (typeof LINE_TABS)[number];
export const validateLineSearch = lenient({ tab: z.enum(LINE_TABS).optional() });

export const validateAuditSearch = lenient({
  actor: optionalText,
  action: optionalText,
  entity: optionalText,
  traceId: optionalText,
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  cursor: optionalText,
});
export type AuditSearch = ReturnType<typeof validateAuditSearch>;

export const validateApplicationsSearch = lenient({
  phase: ApplicationPhaseSchema.optional(),
  recommendation: RecommendationSchema.optional(),
  withAlerts: z.enum(['true']).optional(),
  assignedTo: z.enum(['me']).optional(),
  cursor: optionalText,
});
export type ApplicationsSearch = ReturnType<typeof validateApplicationsSearch>;

export const APPLICATION_TABS = [
  'resumen',
  'documentos',
  'flujo',
  'riesgo',
  'recomendacion',
  'conversaciones',
  'historial',
] as const;
export type ApplicationTab = (typeof APPLICATION_TABS)[number];
export const validateApplicationSearch = lenient({
  tab: z.enum(APPLICATION_TABS).optional(),
  doc: optionalText,
});

export const validateAgentSessionsSearch = lenient({
  agent: AgentRoleSchema.optional(),
  status: AgentSessionStatusSchema.optional(),
  live: z.enum(['true']).optional(),
  withGuards: z.enum(['true']).optional(),
  q: optionalText,
  cursor: optionalText,
});
export type AgentSessionsSearch = ReturnType<typeof validateAgentSessionsSearch>;

export const AFTERSALES_TABS = ['disputas', 'devoluciones', 'conciliacion'] as const;
export type AftersalesTab = (typeof AFTERSALES_TABS)[number];
export const validateAftersalesSearch = lenient({ tab: z.enum(AFTERSALES_TABS).optional() });

export const validateLoginSearch = lenient({ redirect: z.string().startsWith('/').optional() });
